import { useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import FormField from '@components/form_field';
import DatePicker from '@components/date_picker';
import { getBirthDisabledDates, mapOccupationsToOptions } from '@utils/index';
import { useLocales } from '@hooks/helpers/useLocales';
import { useFormContext } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiUserService } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import Select from '@components/common/select';
import { EUserStatus } from '@xeppt/xeppt-sdk/types';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import useResponsive from '@hooks/helpers/useResponsive';

const BusinessSection = () => {
    // const { t } = useTranslation('sections', {
    //     keyPrefix: 'settings.contact'
    // });
    const { labelLocale, submitLocale, requestErrorLocale, requestSuccessLocale } = useLocales();
    const form = useFormContext();
    const [isEdit, setIsEdit] = useState(false);
    const { refetchUser, user, businessData } = useUserContext();
    const { isMobile } = useResponsive();

    const { handleRequest: handleUpdateUser, isLoading } = useApiMutation({
        method: () => {
            const { company_name, trading_name, registration_number, category, size, website } =
                form.getValues();
            return apiUserService.updateProfile({
                company_name,
                trading_name,
                registration_number,
                category,
                size,
                website
            });
        },
        onSuccess: () => {
            requestSuccessLocale('update_profile');
            refetchUser();
            setIsEdit(false);
        },
        onError: requestErrorLocale
    });

    const onSubmit = () => {
        if (!isEdit) {
            setIsEdit(true);
        } else {
            form.trigger().then((isValid) => {
                isValid && handleUpdateUser(undefined);
            });
        }
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.left}>
                        <div className={styles.icon}>
                            <Icon name="business" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <Typography
                                className={styles.title}
                                variant="body1"
                                lh="120%"
                                weight="semibold">
                                Business information
                            </Typography>
                            <p className={styles.description}>
                                {user?.status === EUserStatus.UNVERIFIED
                                    ? 'Make sure your business details are correct. After completing KYC verification, you won’t be able to change them'
                                    : 'Once KYC verification is completed, your business details cannot be edited'}
                            </p>
                        </div>
                    </div>
                    {user?.status === EUserStatus.UNVERIFIED && !isEdit && (
                        <Button
                            leftIcon={isEdit ? undefined : 'edit'}
                            variant="outlined-gold"
                            size="small"
                            onClick={onSubmit}>
                            {!isMobile && (isEdit ? submitLocale('save') : submitLocale('edit'))}
                        </Button>
                    )}
                </div>
                <div className={styles.content}>
                    {isEdit ? (
                        <>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <FormField
                                        name="business.company_name"
                                        renderComponent={(props) => (
                                            <Input
                                                label={labelLocale('company_name')}
                                                {...props}
                                                full
                                                readOnly={!isEdit}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={styles.data_wrapper}>
                                    <FormField
                                        name="business.tradingName"
                                        renderComponent={(props) => (
                                            <Input
                                                label={labelLocale('last_name')}
                                                {...props}
                                                full
                                                readOnly={!isEdit}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <FormField<Date | null>
                                        name="business.registration_number"
                                        renderComponent={(props) => (
                                            <DatePicker
                                                {...props}
                                                label={labelLocale('registration_number')}
                                                isBirthday
                                                full
                                                // readOnly
                                                readOnly={!isEdit}
                                                maxDate={getBirthDisabledDates()}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={styles.data_wrapper}>
                                    <FormField<string | number>
                                        name="business.category"
                                        renderComponent={(props) => (
                                            <Select
                                                {...props}
                                                full
                                                isAllowSearch
                                                readOnly={!isEdit}
                                                label={labelLocale('occupation')}
                                                items={mapOccupationsToOptions()}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <FormField<Date | null>
                                        name="business.size"
                                        renderComponent={(props) => (
                                            <DatePicker
                                                {...props}
                                                label={labelLocale('size')}
                                                isBirthday
                                                full
                                                // readOnly
                                                readOnly={!isEdit}
                                                maxDate={getBirthDisabledDates()}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={styles.data_wrapper}>
                                    <FormField<string | number>
                                        name="business.website"
                                        renderComponent={(props) => (
                                            <Select
                                                {...props}
                                                full
                                                isAllowSearch
                                                readOnly={!isEdit}
                                                label={labelLocale('website')}
                                                items={mapOccupationsToOptions()}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Company name</p>
                                    <p className={styles.value}>{businessData?.company_name}</p>
                                </div>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Trading name</p>
                                    <p className={styles.value}>{businessData?.trading_name}</p>
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Registration number</p>
                                    <p className={styles.value}>
                                        {businessData?.registration_number}
                                    </p>
                                </div>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Category</p>
                                    {/*//@ts-ignore*/}
                                    <p className={styles.value}>{businessData?.category}</p>
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Size</p>
                                    <p className={styles.value}>{businessData?.size}</p>
                                </div>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Website</p>
                                    {/*//@ts-ignore*/}
                                    <p className={styles.value}>{businessData?.website}</p>
                                </div>
                            </div>
                        </>
                    )}
                    {isEdit && (
                        <div className={styles.actions}>
                            <Button
                                onClick={() => {
                                    setIsEdit(false);
                                    form.reset(user);
                                }}
                                variant="outlined-primary"
                                size="medium">
                                {submitLocale('cancel')}
                            </Button>
                            <Button
                                isLoading={isLoading}
                                onClick={onSubmit}
                                variant="primary"
                                size="medium">
                                {submitLocale('save_changes')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BusinessSection;
